/**
 * MyJourney — Feb 2026 R138c · Employee self-service page.
 *
 * Combines onboarding + offboarding into a single page with sub-tabs.
 * Only the tab whose journey is active is shown by default; both are
 * available when the employee has journeys of both kinds. Employees can
 * mark task status + post comments on tasks that HR has explicitly
 * flagged `is_visible_to_employee=true` (backend filters the list — the
 * page never sees internal-only tasks).
 *
 * Read-only mode kicks in when the journey has been marked completed by
 * HR — the employee can still view + comment, just not change status.
 *
 * Route: /app/my/journey (with /app/my/onboarding still routing here for
 * backward compat).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle2, Circle, Clock, MinusCircle, User, ShieldCheck, Calendar, Sparkles, LogOut, MessageSquare, AlertTriangle, Plus, X } from "lucide-react";
import { TaskCommentsPanel } from "@/pages/app/hr/OnboardingTaskItem";

const STATUS_ICON = {
  done: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  in_progress: <Clock className="w-4 h-4 text-amber-500" />,
  skipped: <MinusCircle className="w-4 h-4 text-muted-foreground" />,
  pending: <Circle className="w-4 h-4 text-muted-foreground/50" />,
};
const STATUS_LABEL = {
  done: "Completed", in_progress: "In progress",
  skipped: "Skipped", pending: "Pending",
};

function ProgressBar({ pct }) {
  return (
    <div className="w-full h-2 bg-muted rounded-none border border-border overflow-hidden" data-testid="my-onb-progress-bar">
      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} />
    </div>
  );
}

function TaskRow({ t, readOnly, members, currentUserId, onChanged, focusTask }) {
  const [commentsOpen, setCommentsOpen] = useState(focusTask === t.id);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const overallStatus = t.overall_status || t.status;
  const isFullyDone = t.fully_complete || overallStatus === "done";
  const overdue = t.due_date && t.due_date < today && overallStatus !== "done" && overallStatus !== "skipped";
  const memberById = useMemo(() => Object.fromEntries((members || []).map((m) => [m.user_id || m.id, m])), [members]);
  const nameFor = (uid) => memberById[uid]?.user_name || memberById[uid]?.name || memberById[uid]?.email || (uid ? uid.slice(0, 6) : "—");
  const assigneeLabel = t.assignee_user_id ? nameFor(t.assignee_user_id) : (t.owner_role || "hr");

  const setStatus = async (nextStatus) => {
    if (readOnly) return;
    setBusy(true);
    try {
      await api.patch(`/me/onboarding/tasks/${t.id}`, { status: nextStatus });
      toast.success(`Marked ${STATUS_LABEL[nextStatus].toLowerCase()}`);
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };

  return (
    <li
      className={`grid grid-cols-[1.75rem_minmax(220px,1.5fr)_120px_88px_minmax(120px,1fr)_minmax(120px,1fr)_130px_2.5rem] gap-3 px-3 py-2 border-b border-border last:border-b-0 items-center ${overdue ? "bg-rose-500/[0.04]" : ""}`}
      data-testid={`my-onb-task-${t.id}`}
    >
      <button
        className={`w-5 h-5 border-2 flex items-center justify-center ${t.status === "done" ? "bg-emerald-500 border-emerald-500 text-white" : "border-border hover:border-primary"} disabled:opacity-50`}
        onClick={() => setStatus(t.status === "done" ? "pending" : "done")}
        disabled={busy || readOnly}
        title={readOnly ? "Journey completed — read only" : (t.status === "done" ? "Mark pending" : "Mark done")}
        data-testid={`my-onb-check-${t.id}`}
      >
        {t.status === "done" && <CheckCircle2 className="w-3.5 h-3.5" />}
      </button>
      <div className="min-w-0">
        <div className="font-medium text-sm truncate flex items-center gap-1.5">
          {t.title}
          {overdue && <span className="text-[9px] uppercase tracking-widest bg-rose-500/10 text-rose-700 px-1 border border-rose-500/30 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" />overdue</span>}
          {t.is_required && (
            <span className="text-[9px] uppercase tracking-widest bg-amber-500/10 text-amber-700 px-1 border border-amber-500/30">required</span>
          )}
          {t.added_by_employee && (
            <span className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary px-1 border border-primary/30">added by you</span>
          )}
        </div>
        {t.description && (
          <div className="text-[11px] text-muted-foreground line-clamp-2">{t.description}</div>
        )}
      </div>
      <div className="text-[11px] truncate capitalize" title={assigneeLabel}>{assigneeLabel}</div>
      <div className={`text-[11px] font-mono ${overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
        {t.due_date ? String(t.due_date).slice(0, 10) : "—"}
      </div>
      <div className="text-[11px] truncate flex items-center gap-1">
        {t.reviewer_user_id ? (
          <>
            <span className="truncate" title={nameFor(t.reviewer_user_id)}>{nameFor(t.reviewer_user_id)}</span>
            {t.reviewer_signed_at && <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />}
          </>
        ) : <span className="text-muted-foreground/60 italic">—</span>}
      </div>
      <div className="text-[11px] truncate flex items-center gap-1">
        {t.approver_user_id ? (
          <>
            <ShieldCheck className="w-3 h-3 text-primary shrink-0" />
            <span className="truncate" title={nameFor(t.approver_user_id)}>{nameFor(t.approver_user_id)}</span>
            {t.approver_signed_at && <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />}
          </>
        ) : <span className="text-muted-foreground/60 italic">—</span>}
      </div>
      <div className="text-[11px]">
        <div className={isFullyDone ? "text-emerald-700 font-medium flex items-center gap-1" : "text-muted-foreground flex items-center gap-1"}>
          {STATUS_ICON[overallStatus] || STATUS_ICON.pending}
          <span>{STATUS_LABEL[overallStatus] || "Pending"}</span>
        </div>
        {!isFullyDone && (t.status === "done" || t.reviewer_signed_at || t.approver_signed_at) && (
          <div className="text-[10px] text-amber-700" title="Waiting on remaining sign-offs">
            {(() => {
              const missing = [];
              if (t.status !== "done") missing.push("owner");
              if (t.reviewer_user_id && !t.reviewer_signed_at) missing.push("reviewer");
              if (t.approver_user_id && !t.approver_signed_at) missing.push("approver");
              return `awaiting ${missing.join(" + ")}`;
            })()}
          </div>
        )}
        {isFullyDone && t.completed_at && (
          <div className="text-[10px] text-muted-foreground" title={t.completed_at}>
            {fmtRelative(t.completed_at)}
          </div>
        )}
      </div>
      <Popover open={commentsOpen} onOpenChange={setCommentsOpen}>
        <PopoverTrigger asChild>
          <button
            className="w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted/40"
            title="Comments"
            data-testid={`my-onb-comments-${t.id}`}
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 rounded-none p-0" side="left" align="start">
          <TaskCommentsPanel
            taskId={t.id}
            taskTitle={t.title}
            onClose={() => setCommentsOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </li>
  );
}

function JourneyCard({ j, kind, readOnly, members, currentUserId, onChanged, focusTask }) {
  const Icon = kind === "offboarding" ? LogOut : Sparkles;
  const title = kind === "offboarding" ? "Offboarding" : "Onboarding";
  const emp = j.employee || {};
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const addMyTask = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      await api.post("/me/onboarding/tasks", { kind, title: newTitle.trim(), description: newDesc.trim() });
      toast.success("Task added to your checklist");
      setNewTitle(""); setNewDesc(""); setAddOpen(false);
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-border rounded-none bg-background" data-testid={`my-${kind}-journey-${j.id}`}>
      <header className="p-4 border-b border-border bg-muted/40">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <Icon className="w-3.5 h-3.5" /> {title}
              {j.status && (
                <span className="chip text-[9px] normal-case tracking-normal ml-1">
                  {String(j.status).replace(/_/g, " ")}
                </span>
              )}
              {readOnly && (
                <span className="text-[9px] uppercase tracking-widest bg-muted text-muted-foreground px-1 border border-border">read only</span>
              )}
            </div>
            <h3 className="text-lg font-semibold mt-0.5 truncate">
              {emp.full_name ? `${emp.full_name}\u2019s ${title.toLowerCase()}` : title}
            </h3>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
              {emp.designation && <span>{emp.designation}</span>}
              {emp.emp_id && <span>· #{emp.emp_id}</span>}
              {kind === "onboarding" && emp.joining_date && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Joined {emp.joining_date.slice(0, 10)}</span>
              )}
              {kind === "offboarding" && emp.exit_date && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Exit {emp.exit_date.slice(0, 10)}</span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0" data-testid={`my-${kind}-progress-${j.id}`}>
            <div className="text-3xl font-bold tabular-nums">{j.pct_complete || 0}%</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
              {j.task_done || 0} of {j.task_total || 0} steps
            </div>
            {!readOnly && (
              <Popover open={addOpen} onOpenChange={setAddOpen}>
                <PopoverTrigger asChild>
                  <button className="mt-2 text-[11px] text-primary hover:underline flex items-center gap-1" data-testid={`my-${kind}-add-task-btn`}>
                    <Plus className="w-3 h-3" /> Add my own task
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 rounded-none p-0" side="bottom" align="end" data-testid={`my-${kind}-add-task-popover`}>
                  <div className="border-2 border-primary">
                    <div className="px-3 py-2 border-b border-border bg-primary/10 flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest">Add follow-up for you</div>
                      <button onClick={() => setAddOpen(false)}><X className="w-3.5 h-3.5 text-muted-foreground" /></button>
                    </div>
                    <div className="p-3 space-y-2">
                      <Input placeholder="What do you want to remember?" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="rounded-none h-9" autoFocus data-testid="my-add-task-title" />
                      <Textarea placeholder="Optional notes" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={2} className="rounded-none text-sm resize-none" />
                      <div className="text-[10px] text-muted-foreground italic">HR can see this task. You can edit the status + notes anytime — title stays locked once added.</div>
                    </div>
                    <div className="px-3 py-2 border-t border-border flex justify-end gap-2">
                      <Button variant="ghost" size="sm" className="rounded-none h-7" onClick={() => setAddOpen(false)}>Cancel</Button>
                      <Button size="sm" className="rounded-none h-7" onClick={addMyTask} disabled={saving || !newTitle.trim()} data-testid="my-add-task-save">
                        {saving ? "Adding…" : "Add task"}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        <div className="mt-3"><ProgressBar pct={j.pct_complete || 0} /></div>
      </header>

      {(j.tasks || []).length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          Nothing here yet — HR keeps some steps internal and only shows you what needs your attention.
        </div>
      ) : (
        <ul data-testid={`my-${kind}-tasks-${j.id}`}>
          <li className="grid grid-cols-[1.75rem_minmax(220px,1.5fr)_120px_88px_minmax(120px,1fr)_minmax(120px,1fr)_130px_2.5rem] gap-3 px-3 py-2 border-b border-border bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            <div />
            <div>Task</div>
            <div>Owner</div>
            <div>Due</div>
            <div>Reviewer</div>
            <div>Approver</div>
            <div>Status</div>
            <div />
          </li>
          {(j.tasks || []).map((t) => <TaskRow key={t.id} t={t} readOnly={readOnly} members={members} currentUserId={currentUserId} onChanged={onChanged} focusTask={focusTask} />)}
        </ul>
      )}
    </section>
  );
}

export default function MyJourney() {
  const { activeWorkspace, user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [tab, setTab] = useState("onboarding");
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [members, setMembers] = useState([]);
  const [searchParams] = useSearchParams();
  const focusTask = searchParams.get("task");

  const load = async () => {
    try {
      const { data } = await api.get("/me/onboarding");
      setData(data || { onboarding: [], offboarding: [] });
      // Fetch workspace members once so TaskRow can label reviewer /
      // approver / assignee uids with names — safe optional fetch, the
      // fallback shows the raw id fragment when missing.
      const anyJourney = (data?.onboarding?.[0] || data?.offboarding?.[0]);
      if (anyJourney?.workspace_id && members.length === 0) {
        try {
          const r = await api.get(`/workspaces/${anyJourney.workspace_id}/members`);
          setMembers(r.data || []);
        } catch { /* members are optional */ }
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load your journey"));
      setData({ onboarding: [], offboarding: [] });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const onb = data?.onboarding || [];
  const off = data?.offboarding || [];
  const empty = !busy && onb.length === 0 && off.length === 0;

  // Auto-pick the tab that actually has an active journey when the page
  // first hydrates.
  const hasActiveOnb = onb.some((j) => j.status !== "completed");
  const hasActiveOff = off.some((j) => j.status !== "completed");
  useMemo(() => {
    if (!busy) {
      if (!hasActiveOnb && hasActiveOff) setTab("offboarding");
      else if (hasActiveOnb) setTab("onboarding");
    }
  }, [busy, hasActiveOnb, hasActiveOff]);

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={(user?.name || user?.email || "").split(" ")[0] ? `HELLO, ${(user?.name || user?.email || "").split(" ")[0].toUpperCase()}` : "WELCOME"}
        title={activeWorkspace?.name || "Your workspace"}
        description="Here’s everything HR has shared with you — check things off as you go and use comments to ask questions."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-6">
        {busy && (
          <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="my-onb-loading">
            Loading your checklist…
          </div>
        )}
        {empty && (
          <div className="border border-border p-8 text-center" data-testid="my-onb-empty">
            <User className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
            <div className="text-sm font-semibold">Nothing in flight</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              You don&rsquo;t have an active onboarding or offboarding journey right now. If you believe this is wrong, please contact your HR partner.
            </p>
            <div className="mt-3">
              <Link to="/app/dashboard" className="text-[11px] text-primary hover:underline">Back to dashboard →</Link>
            </div>
          </div>
        )}

        {!empty && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-none">
              {onb.length > 0 && (
                <TabsTrigger value="onboarding" className="rounded-none" data-testid="my-journey-tab-onboarding">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Onboarding
                </TabsTrigger>
              )}
              {off.length > 0 && (
                <TabsTrigger value="offboarding" className="rounded-none" data-testid="my-journey-tab-offboarding">
                  <LogOut className="w-3.5 h-3.5 mr-1.5" /> Offboarding
                </TabsTrigger>
              )}
            </TabsList>
            {onb.length > 0 && (
              <TabsContent value="onboarding" className="pt-4 space-y-4">
                {onb.map((j) => (
                  <JourneyCard
                    key={j.id} j={j} kind="onboarding"
                    readOnly={j.status === "completed"}
                    members={members}
                    currentUserId={user?.id}
                    onChanged={load}
                    focusTask={focusTask}
                  />
                ))}
              </TabsContent>
            )}
            {off.length > 0 && (
              <TabsContent value="offboarding" className="pt-4 space-y-4">
                {off.map((j) => (
                  <JourneyCard
                    key={j.id} j={j} kind="offboarding"
                    readOnly={j.status === "completed"}
                    members={members}
                    currentUserId={user?.id}
                    onChanged={load}
                    focusTask={focusTask}
                  />
                ))}
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </div>
  );
}
